import { Sun, Moon, Laptop } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useTheme } from '@/components/ThemeProvider';

export default function PreviewUI() {
  const { theme, setTheme } = useTheme();

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="mx-auto max-w-3xl space-y-6">
        <header className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              Preview UI — Tailwind + shadcn
            </h1>
            <p className="text-sm text-muted-foreground">
              Page sandbox pour valider la stack Phase 1 (Tailwind, shadcn, theme)
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant={theme === 'light' ? 'default' : 'outline'}
              size="icon"
              onClick={() => setTheme('light')}
              aria-label="Light theme"
            >
              <Sun className="h-4 w-4" />
            </Button>
            <Button
              variant={theme === 'dark' ? 'default' : 'outline'}
              size="icon"
              onClick={() => setTheme('dark')}
              aria-label="Dark theme"
            >
              <Moon className="h-4 w-4" />
            </Button>
            <Button
              variant={theme === 'system' ? 'default' : 'outline'}
              size="icon"
              onClick={() => setTheme('system')}
              aria-label="System theme"
            >
              <Laptop className="h-4 w-4" />
            </Button>
          </div>
        </header>

        <Card>
          <CardHeader>
            <CardTitle>Test card</CardTitle>
            <CardDescription>
              Si tu vois ce composant correctement stylé en clair / sombre, la
              Phase 1 fonctionne.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nom</Label>
              <Input id="name" placeholder="Saisir un nom" />
            </div>
            <div className="space-y-2">
              <Label>Sélecteur</Label>
              <Select>
                <SelectTrigger>
                  <SelectValue placeholder="Choisir une option" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="a">Option A</SelectItem>
                  <SelectItem value="b">Option B</SelectItem>
                  <SelectItem value="c">Option C</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
          <CardFooter className="gap-2">
            <Button>Action primaire</Button>
            <Button variant="secondary">Secondaire</Button>
            <Button variant="outline">Outline</Button>
            <Button variant="destructive">Destructive</Button>
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="ghost">Ouvrir dialog</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Dialog test</DialogTitle>
                  <DialogDescription>
                    Composant Dialog avec overlay, focus trap, animation.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button>OK</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </CardFooter>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Tokens design</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-3 gap-2 sm:grid-cols-6">
            {[
              ['background', 'foreground'],
              ['primary', 'primary-foreground'],
              ['secondary', 'secondary-foreground'],
              ['muted', 'muted-foreground'],
              ['accent', 'accent-foreground'],
              ['destructive', 'destructive-foreground'],
            ].map(([bg, fg]) => (
              <div
                key={bg}
                className={`flex h-16 items-center justify-center rounded-md bg-${bg} text-${fg} text-xs font-medium`}
              >
                {bg}
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
